/**
 * Brand Editor Tabs (spec 0020) — Preview/Theme/Translations/Advanced tabs sharing state.
 *
 * Preview: scaled BrandLoginMock with faux browser-tab bar, rendering live from form.
 * Theme (SASADM-0154): the brand theme editor — the one editor for colours and fonts.
 * Translations (SASADM-0155): the es/fr/pt copy grid on `copy-locales`.
 * Advanced: full 0018 form minus the template picker (moved to top row).
 *
 * Spec 0021: Edit-mode slots in Preview — click visual elements to edit in popovers.
 * SASADM-0154: the popovers no longer edit colours or fonts; colour-only slots
 * point to the Theme tab instead.
 */
import type React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';
import {
  BrandLoginMock,
  PREVIEW_VIEWPORT,
  type BrandMockConfig,
  type EditModeHooks,
  type SlotField,
} from './BrandLoginMock';
import { BrandImageField } from './BrandImageField';
import { GuestSignupHint } from './GuestSignupHint';
import { BrandThemePanel } from './BrandThemePanel';
import { BrandTranslationsPanel } from './BrandTranslationsPanel';
import type { BrandDto, ProductDto } from '@/api/types';
import type { ApiError } from '@/api/errors';
import type { EditorState } from './types';
import { SUPPORTED_PROVIDERS } from './types';

const PREVIEW_TAB = 0;
const THEME_TAB = 1;
const TRANSLATIONS_TAB = 2;
const ADVANCED_TAB = 3;

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} aria-labelledby={`tab-${index}`}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface BrandEditorTabsProps {
  state: EditorState;
  setState: React.Dispatch<React.SetStateAction<EditorState>>;
  brand: BrandDto | null;
  products?: ProductDto[];
  advancedContent: ReactNode;
  // Spec 0021 — hero upload handler
  onUploadHero?: (file: File) => Promise<void>;
  heroUploadPending?: boolean;
  heroUploadError?: ApiError | null;
  // SASADM-0154 — Theme tab inputs: the loader's tenant count, a token the page
  // bumps when a legacy save/upload makes the theme etag stale, and a callback
  // after a theme save (the page refetches the brand).
  tenantsUsingTotal?: number;
  themeStaleToken?: number;
  onThemeSaved?: () => void;
  // SASADM-0155 — Translations tab inputs: a token the page bumps after a legacy
  // save (re-GET copy-locales when next shown), and a callback after a
  // translations save (it changed the theme etag, so the page marks it stale).
  copyLocalesStaleToken?: number;
  onTranslationsSaved?: () => void;
}

export function BrandEditorTabs({ state, setState, brand, products = [], advancedContent, onUploadHero, heroUploadPending, heroUploadError, tenantsUsingTotal = 0, themeStaleToken = 0, onThemeSaved, copyLocalesStaleToken = 0, onTranslationsSaved }: BrandEditorTabsProps) {
  const { t } = useCopy();
  const [tab, setTab] = useState(PREVIEW_TAB);
  // The Theme and Translations panels mount on first visit and then stay
  // mounted (hidden), so their unsaved edits survive a switch to another tab.
  const [themeVisited, setThemeVisited] = useState(false);
  const [translationsVisited, setTranslationsVisited] = useState(false);
  function selectTab(next: number) {
    if (next === THEME_TAB) setThemeVisited(true);
    if (next === TRANSLATIONS_TAB) setTranslationsVisited(true);
    setTab(next);
  }
  // Spec 0030 — the effective product supplies the guest-signup URL/label.
  const guestProduct = products.find((p) => p.id === state.productId) ?? null;

  // Spec 0027 — preview zoom: Fit (scale the 1440×900 viewport to the column,
  // default) | 100% (unscaled in a scrollable frame).
  const [zoomMode, setZoomMode] = useState<'fit' | 'full'>('fit');
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [fitScale, setFitScale] = useState(1);
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setFitScale(w / PREVIEW_VIEWPORT.width);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoomMode]);

  // Spec 0021 — Popover state for slot editing
  const [popoverAnchor, setPopoverAnchor] = useState<{ field: SlotField; anchorEl: HTMLElement } | null>(null);
  const isNew = !brand;

  // Build the preview config from current form state
  // Reuses the same payload builder logic as the pop-out preview button
  function buildPreviewConfig(): BrandMockConfig {
    // Helper to compose a URL from image + baseUrl (or fall back to global if base is empty)
    function composeImageUrl(image: string, baseUrl: string): string {
      if (!image) return '';
      const effectiveBase = baseUrl || brand?.globalAssetBaseUrl || '';
      if (!effectiveBase) return '';
      const cleanBase = effectiveBase.endsWith('/') ? effectiveBase.slice(0, -1) : effectiveBase;
      return `${cleanBase}/${image}`;
    }

    const logoUrl = composeImageUrl(state.logoImage, state.logoBaseUrl) || brand?.logoUrl;
    const heroImageUrl = composeImageUrl(state.heroImage, state.heroBaseUrl) || brand?.heroImageUrl;

    const config: BrandMockConfig = {
      displayName: state.displayName,
      colors: state.colors,
      fonts: state.fonts,
      authProviders: state.authProviders,
      signupEnabled: state.signupEnabled,
      passwordResetEnabled: state.passwordResetEnabled,
    };

    // Optional fields
    if (state.shortName) config.shortName = state.shortName;
    if (logoUrl) config.logoUrl = logoUrl;
    if (state.template) config.template = state.template;
    if (state.tagline) config.tagline = state.tagline;
    if (state.heroCopy) config.heroCopy = state.heroCopy;
    if (state.location) config.location = state.location;
    if (heroImageUrl) config.heroImageUrl = heroImageUrl;
    if (state.primaryColorHover) config.primaryColorHover = state.primaryColorHover;
    if (state.loginTitle) config.copyStrings = { login_title: state.loginTitle };
    // Spec 0030 — guest-signup toggle + effective label (from the product).
    config.guestSignupEnabled = state.guestSignupEnabled;
    if (guestProduct?.guestSignupLabel) config.guestSignupLabel = guestProduct.guestSignupLabel;

    return config;
  }

  const previewConfig = buildPreviewConfig();
  // Effective login title for the faux tab bar (fallback chain same as the mock)
  const effectiveLoginTitle = previewConfig.copyStrings?.login_title || previewConfig.shortName || previewConfig.displayName || 'Sign in';

  // Spec 0022 — slot label lookup (en only)
  const SLOT_LABELS: Record<SlotField, string> = {
    heroImage: t('brands.slots.heroImage', 'Hero image'),
    logo: t('brands.slots.logo', 'Logo'),
    favicon: t('brands.slots.favicon', 'Favicon'),
    heading: t('brands.slots.heading', 'Login title'),
    subtitle: t('brands.slots.subtitle', 'Short name'),
    tagline: t('brands.slots.tagline', 'Tagline'),
    heroCopy: t('brands.slots.heroCopy', 'Hero copy'),
    location: t('brands.slots.location', 'Location'),
    signInButton: t('brands.slots.signInButton', 'Sign-in button'),
    pageBackground: t('brands.slots.pageBackground', 'Page background'),
    cardBorder: t('brands.slots.cardBorder', 'Card border'),
    googleButton: t('brands.slots.googleButton', 'Google sign-in'),
    signupLink: t('brands.slots.signupLink', 'Sign-up link'),
    forgotPassword: t('brands.slots.forgotPassword', 'Password reset link'),
    guestSignup: t('brands.slots.guestSignup', 'Guest signup'),
  };

  // Spec 0021 — Edit-mode hooks for Preview tab
  const editMode: EditModeHooks = {
    onSlotClick: (field, anchorEl) => {
      setPopoverAnchor({ field, anchorEl });
    },
    getSlotLabel: (field) => SLOT_LABELS[field] ?? field,
    // Spec 0023 — toggle slots render ghosted when their feature is off
    getSlotState: (field) => {
      switch (field) {
        case 'googleButton':
          return state.authProviders.includes('google') ? 'active' : 'ghosted';
        case 'signupLink':
          return state.signupEnabled ? 'active' : 'ghosted';
        case 'forgotPassword':
          return state.passwordResetEnabled ? 'active' : 'ghosted';
        case 'guestSignup':
          return state.guestSignupEnabled ? 'active' : 'ghosted';
        default:
          return 'active';
      }
    },
  };

  // Spec 0022 — state updater for text slot popovers
  function setField<K extends keyof EditorState>(key: K, value: EditorState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }
  // Spec 0023 — provider toggle
  function toggleProvider(p: string, on: boolean) {
    setState((s) => ({
      ...s,
      authProviders: on
        ? Array.from(new Set([...s.authProviders, p]))
        : s.authProviders.filter((x) => x !== p),
    }));
  }

  function handleClosePopover() {
    setPopoverAnchor(null);
  }

  // SASADM-0154 — a colour-only slot points to the Theme tab instead of fields.
  function themeTabPointer() {
    return (
      <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="body2">
          {t('brands.theme.editInThemeTab', 'Edit colours in the Theme tab')}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setPopoverAnchor(null);
            selectTab(THEME_TAB);
          }}
        >
          {t('brands.theme.openThemeTab', 'Open the Theme tab')}
        </Button>
      </Stack>
    );
  }

  return (
    <Box>
      <Tabs value={tab} onChange={(_, val: number) => selectTab(val)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('brands.tabs.preview', 'Preview')} id="tab-0" aria-controls="tabpanel-0" />
        <Tab label={t('brands.tabs.theme', 'Theme')} id="tab-1" aria-controls="tabpanel-1" />
        <Tab label={t('brands.tabs.translations', 'Translations')} id="tab-2" aria-controls="tabpanel-2" />
        <Tab label={t('brands.tabs.advanced', 'Advanced')} id="tab-3" aria-controls="tabpanel-3" />
      </Tabs>

      <TabPanel value={tab} index={PREVIEW_TAB}>
        {/* Preview tab: faux browser-tab bar + scaled BrandLoginMock */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {/* Faux browser tab bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
            {/* Favicon thumbnail — clickable slot */}
            <Box
              data-slot="favicon"
              onClick={(e) => {
                e.stopPropagation();
                editMode.onSlotClick('favicon', e.currentTarget as HTMLElement);
              }}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                  outline: '2px dashed',
                  outlineColor: 'primary.main',
                  outlineOffset: '1px',
                },
              }}
            >
              {previewConfig.logoUrl ? (
                <Box
                  component="img"
                  src={previewConfig.logoUrl}
                  alt=""
                  sx={{ width: 16, height: 16, objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ width: 16, height: 16, bgcolor: 'grey.300', borderRadius: 0.5 }} />
              )}
            </Box>
            {/* Login title */}
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {effectiveLoginTitle}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {/* Spec 0027 — Fit / 100% zoom toggle */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={zoomMode}
              onChange={(_e, val: 'fit' | 'full' | null) => {
                if (val) setZoomMode(val);
              }}
              aria-label={t('brands.preview.zoom', 'Zoom')}
            >
              <ToggleButton value="fit">{t('brands.preview.fit', 'Fit')}</ToggleButton>
              <ToggleButton value="full">{t('brands.preview.fullSize', '100%')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Spec 0027 — true 1440×900 virtual viewport.
              Fit: scale the whole viewport to the column (aspect-ratio box, no
              clipping). 100%: unscaled in a scrollable frame. */}
          {zoomMode === 'fit' ? (
            <Box
              ref={stageRef}
              data-testid="preview-stage-fit"
              sx={{
                width: '100%',
                aspectRatio: `${PREVIEW_VIEWPORT.width} / ${PREVIEW_VIEWPORT.height}`,
                overflow: 'hidden',
                position: 'relative',
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transformOrigin: 'top left',
                  transform: `scale(${fitScale})`,
                }}
              >
                <BrandLoginMock config={previewConfig} editMode={editMode} viewport={PREVIEW_VIEWPORT} />
              </Box>
            </Box>
          ) : (
            <Box
              data-testid="preview-stage-full"
              sx={{
                maxHeight: '70vh',
                overflow: 'auto',
                bgcolor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <BrandLoginMock config={previewConfig} editMode={editMode} viewport={PREVIEW_VIEWPORT} />
            </Box>
          )}

          {/* Spec 0021 — Slot editing popover */}
          {popoverAnchor && (
            <Popover
              open
              anchorEl={popoverAnchor.anchorEl}
              onClose={handleClosePopover}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'center',
              }}
            >
              <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
                {popoverAnchor.field === 'heroImage' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.heroImage', 'Hero image')}
                    </Typography>
                    <BrandImageField
                      assetType="hero"
                      image={state.heroImage}
                      baseUrl={state.heroBaseUrl}
                      composedUrl={
                        state.heroImage
                          ? `${state.heroBaseUrl || brand?.globalAssetBaseUrl || ''}${
                              state.heroBaseUrl || brand?.globalAssetBaseUrl ? '/' : ''
                            }${state.heroImage}`
                          : brand?.heroImageUrl || ''
                      }
                      globalAssetBaseUrl={brand?.globalAssetBaseUrl ?? ''}
                      onChange={(image, baseUrl) => {
                        setState((s) => ({ ...s, heroImage: image, heroBaseUrl: baseUrl }));
                      }}
                      {...(onUploadHero && { onUpload: onUploadHero })}
                      isNew={isNew}
                      {...(heroUploadPending !== undefined && { uploadPending: heroUploadPending })}
                      {...(heroUploadError !== undefined && { uploadError: heroUploadError })}
                      {...(state.template === 'split-hero' && { sizeHint: '≥1600×2000 px, portrait · JPEG' })}
                      {...(state.template === 'backdrop' && { sizeHint: '1920×1080–2560×1440 px, landscape · JPEG' })}
                    />
                  </>
                )}
                {popoverAnchor.field === 'logo' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.logo', 'Logo')}
                    </Typography>
                    <BrandImageField
                      assetType="logo"
                      image={state.logoImage}
                      baseUrl={state.logoBaseUrl}
                      composedUrl={
                        state.logoImage
                          ? `${state.logoBaseUrl || brand?.globalAssetBaseUrl || ''}${
                              state.logoBaseUrl || brand?.globalAssetBaseUrl ? '/' : ''
                            }${state.logoImage}`
                          : brand?.logoUrl || ''
                      }
                      globalAssetBaseUrl={brand?.globalAssetBaseUrl ?? ''}
                      onChange={(image, baseUrl) => {
                        setState((s) => ({ ...s, logoImage: image, logoBaseUrl: baseUrl }));
                      }}
                      isNew={isNew}
                    />
                  </>
                )}
                {popoverAnchor.field === 'favicon' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.favicon', 'Favicon')}
                    </Typography>
                    <BrandImageField
                      assetType="favicon"
                      image={state.faviconImage}
                      baseUrl={state.faviconBaseUrl}
                      composedUrl={
                        state.faviconImage
                          ? `${state.faviconBaseUrl || brand?.globalAssetBaseUrl || ''}${
                              state.faviconBaseUrl || brand?.globalAssetBaseUrl ? '/' : ''
                            }${state.faviconImage}`
                          : brand?.faviconUrl || ''
                      }
                      globalAssetBaseUrl={brand?.globalAssetBaseUrl ?? ''}
                      onChange={(image, baseUrl) => {
                        setState((s) => ({ ...s, faviconImage: image, faviconBaseUrl: baseUrl }));
                      }}
                      isNew={isNew}
                    />
                  </>
                )}

                {/* Spec 0022 — Text slots */}
                {popoverAnchor.field === 'heading' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.heading', 'Login title')}
                    </Typography>
                    <TextField
                      fullWidth
                      label={t('brands.fields.loginTitle', 'Login title')}
                      value={state.loginTitle}
                      onChange={(e) => setField('loginTitle', e.target.value)}
                      placeholder={t('brands.placeholders.loginTitle', 'Sign in to {short name}')}
                      helperText={
                        state.loginTitle
                          ? ''
                          : t('brands.slots.headingCurrentlyShowing', 'Currently showing: {{value}}', {
                              value: state.shortName || state.displayName,
                            })
                      }
                    />
                  </>
                )}
                {popoverAnchor.field === 'subtitle' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.subtitle', 'Short name')}
                    </Typography>
                    <TextField
                      fullWidth
                      label={t('brands.fields.shortName', 'Product short name')}
                      value={state.shortName}
                      onChange={(e) => setField('shortName', e.target.value)}
                      helperText={t(
                        'brands.fields.shortNameHelper',
                        'Used in the app bar, tab title, and installed-app name.',
                      )}
                    />
                  </>
                )}
                {popoverAnchor.field === 'tagline' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.tagline', 'Tagline')}
                    </Typography>
                    <TextField
                      fullWidth
                      label={t('brands.fields.tagline', 'Tagline')}
                      value={state.tagline}
                      onChange={(e) => setField('tagline', e.target.value)}
                      placeholder={t('brands.placeholders.tagline', 'Bookings made simple')}
                    />
                  </>
                )}
                {popoverAnchor.field === 'heroCopy' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.heroCopy', 'Hero copy')}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={t('brands.fields.heroCopy', 'Hero copy')}
                      value={state.heroCopy}
                      onChange={(e) => setField('heroCopy', e.target.value)}
                      placeholder={t('brands.placeholders.heroCopy', 'Get started today.')}
                    />
                  </>
                )}
                {popoverAnchor.field === 'location' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.location', 'Location')}
                    </Typography>
                    <TextField
                      fullWidth
                      label={t('brands.fields.location', 'Location')}
                      value={state.location}
                      onChange={(e) => setField('location', e.target.value)}
                      placeholder={t('brands.placeholders.location', 'San Francisco')}
                    />
                  </>
                )}

                {/* Spec 0022 — Color slots: SASADM-0154 moved their colours to the Theme tab */}
                {popoverAnchor.field === 'signInButton' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.signInButton', 'Sign-in button')}
                    </Typography>
                    {themeTabPointer()}
                  </>
                )}
                {popoverAnchor.field === 'pageBackground' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.pageBackground', 'Page background')}
                    </Typography>
                    {themeTabPointer()}
                  </>
                )}
                {popoverAnchor.field === 'cardBorder' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.cardBorder', 'Card border')}
                    </Typography>
                    {themeTabPointer()}
                  </>
                )}

                {/* Spec 0023 — Toggle slots (ghosted-when-off) */}
                {popoverAnchor.field === 'googleButton' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.googleButton', 'Google sign-in')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('brands.fields.authProviders', 'Auth providers')}
                    </Typography>
                    <Stack>
                      {SUPPORTED_PROVIDERS.map((p) => (
                        <FormControlLabel
                          key={p}
                          control={
                            <Checkbox
                              checked={state.authProviders.includes(p)}
                              onChange={(e) => toggleProvider(p, e.target.checked)}
                            />
                          }
                          label={p}
                        />
                      ))}
                    </Stack>
                  </>
                )}
                {popoverAnchor.field === 'signupLink' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.signupLink', 'Sign-up link')}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.signupEnabled}
                          onChange={(e) => setField('signupEnabled', e.target.checked)}
                        />
                      }
                      label={t('brands.fields.signupEnabled', 'Signup enabled')}
                    />
                  </>
                )}
                {popoverAnchor.field === 'forgotPassword' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.forgotPassword', 'Password reset link')}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.passwordResetEnabled}
                          onChange={(e) => setField('passwordResetEnabled', e.target.checked)}
                        />
                      }
                      label={t('brands.fields.passwordResetEnabled', 'Password reset enabled')}
                    />
                  </>
                )}
                {popoverAnchor.field === 'guestSignup' && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('brands.slots.guestSignup', 'Guest signup')}
                    </Typography>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={state.guestSignupEnabled}
                            onChange={(e) => setField('guestSignupEnabled', e.target.checked)}
                          />
                        }
                        label={t(
                          'brands.fields.guestSignupEnabled',
                          "Show 'Continue as guest' link on the login page",
                        )}
                      />
                      <GuestSignupHint product={guestProduct} />
                    </Stack>
                  </>
                )}
              </Box>
            </Popover>
          )}
        </Paper>
      </TabPanel>

      {/* SASADM-0154 — Theme tab (existing brands only); kept mounted once visited */}
      <div role="tabpanel" hidden={tab !== THEME_TAB} aria-labelledby={`tab-${THEME_TAB}`}>
        {brand ? (
          themeVisited ? (
            <BrandThemePanel
              key={brand.id}
              brandId={brand.id}
              tenantsUsingTotal={tenantsUsingTotal}
              staleToken={themeStaleToken}
              active={tab === THEME_TAB}
              onSaved={() => onThemeSaved?.()}
              name={state.shortName || state.displayName}
            />
          ) : null
        ) : tab === THEME_TAB ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2">
              {t('brands.theme.saveBrandFirst', 'Save the brand first.')}
            </Typography>
          </Paper>
        ) : null}
      </div>

      {/* SASADM-0155 — Translations tab (existing brands only); kept mounted once visited */}
      <div role="tabpanel" hidden={tab !== TRANSLATIONS_TAB} aria-labelledby={`tab-${TRANSLATIONS_TAB}`}>
        {brand ? (
          translationsVisited ? (
            <BrandTranslationsPanel
              key={brand.id}
              brandId={brand.id}
              staleToken={copyLocalesStaleToken}
              active={tab === TRANSLATIONS_TAB}
              onSaved={() => onTranslationsSaved?.()}
            />
          ) : null
        ) : tab === TRANSLATIONS_TAB ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2">
              {t('brands.translations.saveBrandFirst', 'Save the brand first.')}
            </Typography>
          </Paper>
        ) : null}
      </div>

      <TabPanel value={tab} index={ADVANCED_TAB}>
        {/* Advanced tab: full 0018 form minus the template picker */}
        {advancedContent}
      </TabPanel>
    </Box>
  );
}
